import { productPricingService } from "../../services/productPricingService";
import { DisplayVariant, PRICE_UPDATE_EVENT, PriceDisplayType, ProductPricingInput, SelectionsWithQuantity } from "../../types/pricing/price-display.types";
import { EstimatedPrice, ProductPriceDetails, QuantityMetaData } from "../../types/pricing/product-price-details.types";
import { ensurePricingContext } from "../../util/ensurePricingContext";
import { getPricingContext } from "../../util/pricing-context";
import { calculateEvaluatedQuantity } from "../../util/price-extraction-helper";
import { getPriceQueryId } from "../../util/price-queryId";
import { sanitizeSelections } from "../../util/sanitizeSelections";
import { priceEventTarget } from "./getProductPriceData";

export type FullProductPriceDetailsInput = {
  mpvId?: string;
  productKey?: string;
  productId?: string;
  version?: number;
  selections?: SelectionsWithQuantity;
  quantity?: number;
  alternateSelections?: SelectionsWithQuantity;
  displayVariant?: DisplayVariant;
  requestedPriceDisplayStrategy?: PriceDisplayType;
  docRefUrl?: string;
  assumeDocRequiresCertification?: boolean;
  developmentMode?: boolean
  tenant?: string;
  locale?: string;
  /**
   * When false, skips the fetch (no invalidation, no queuePriceQuery) but still
   * reads from the existing cache and listens for PRICE_UPDATE_EVENT — avoiding
   * duplicate requests or race conditions when another component owns the fetch.
   * Defaults to true.
   */
  shouldFetch?: boolean;
};

export type FullProductPriceDetailsResult = {
  estimatedPrice: EstimatedPrice;
  quantity: number;
  currency: string;
  fractionDigits: number;
  quantityMetaData?: QuantityMetaData;
  alternateQuantityMetaData?: QuantityMetaData;
  displayStrategy: PriceDisplayType;
  priceDisplayGroup?: string;
  productId?: string;
  mpvId?: string;
  url: string;
};

const buildQueryInput = (input: FullProductPriceDetailsInput): Pick<
  ProductPricingInput,
  "mpvId" | "productId" | "version" | "selections" | "alternateSelections" | "displayVariant" | "requestedPriceDisplayStrategy" | "docRefUrl" | "assumeDocRequiresCertification"
> => {
  const { mpvId, productId, productKey, version, selections, quantity, alternateSelections, displayVariant, requestedPriceDisplayStrategy, docRefUrl, assumeDocRequiresCertification } = input;

  const mergedSelections = sanitizeSelections(selections, quantity);

  return {
    mpvId,
    productId: productId ?? productKey,
    version,
    selections: mergedSelections,
    alternateSelections,
    displayVariant,
    requestedPriceDisplayStrategy,
    docRefUrl,
    assumeDocRequiresCertification,
  };
};

/**
 * Returns pricing details for a given set of query props (the same props used
 * by PriceDisplay). Returns the EstimatedPrice for the requested quantity, or
 * the first available entry if the requested quantity is not found.
 *
 * When no cache entry exists, triggers a fetch via productPricingService —
 * requires the service to be initialised (tenant/locale/pricingContext set by
 * a PriceDisplay rendered elsewhere on the page).
 *
 * When docRefUrl is present the cached result is always invalidated before the
 * lookup, matching PriceExtractionWrapper's behaviour: the document content can
 * change at the same URL so every call fetches fresh data.
 */
export const getFullProductPriceDetails = async (
  input: FullProductPriceDetailsInput,
  timeoutMs = 5000,
): Promise<FullProductPriceDetailsResult> => {
  const queryInput = buildQueryInput(input);
  const queryId = getPriceQueryId(queryInput);
  const requestedQuantity = input.quantity ?? (input.selections?.quantity ? Number(input.selections.quantity) : undefined);

  if (!queryId) {
    return Promise.reject(new Error("Unable to generate queryId from provided input"));
  }

  const shouldFetch = input.shouldFetch !== false;

  if (shouldFetch) {
    // docRefUrl points to document content that can be replaced at the same URL.
    // Always invalidate so the fetch below retrieves fresh data.
    if (input.docRefUrl) {
      productPricingService.invalidateQuery(queryId);
    }

    const tenant = input.tenant || productPricingService.tenant;
    const locale = input.locale || productPricingService.locale;
    productPricingService.tenant = tenant;
    productPricingService.locale = locale;
    await ensurePricingContext(tenant, locale, input.developmentMode);
    if (!productPricingService.pricingContext?.isInitialized) {
      productPricingService.pricingContext = getPricingContext();
    }
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Price details unavailable after timeout"));
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(timeoutId);
      priceEventTarget.removeEventListener(PRICE_UPDATE_EVENT, listener);
    };

    const tryResolve = (details: Record<string, ProductPriceDetails>): boolean => {
      const match = details[queryId];
      if (!match || match.isError) return false;

      const evaluatedQuantity = calculateEvaluatedQuantity(match, requestedQuantity?.toString(), undefined);
      const estimatedPrice = match.prices.estimatedPrices[Number(evaluatedQuantity)];
      if (!estimatedPrice) return false;

      cleanup();
      resolve({
        estimatedPrice,
        quantity: Number(evaluatedQuantity),
        currency: match.prices.currency,
        fractionDigits: match.prices.fractionDigits,
        quantityMetaData: match.quantityMetaData,
        alternateQuantityMetaData: match.alternateQuantityMetaData,
        displayStrategy: match.displayStrategy,
        priceDisplayGroup: match.priceDisplayGroup,
        productId: match.productId,
        mpvId: match.mpvId,
        url: match.prices.url,
      });
      return true;
    };

    const listener = (event: Event) => {
      const { detail } = event as CustomEvent<Record<string, ProductPriceDetails>>;
      tryResolve(detail);
    };

    // Check current cache first (will be empty when docRefUrl was just invalidated above)
    if (tryResolve(productPricingService.pricingDetails)) {
      return;
    }

    // No cache entry — trigger the fetch (only when shouldFetch) and wait for PRICE_UPDATE_EVENT.
    // When shouldFetch is false we still listen: another component may trigger the fetch and
    // we want to pick up the result without causing a duplicate request or race condition.
    if (shouldFetch) {
      productPricingService.queuePriceQuery(queryInput);
    }
    priceEventTarget.addEventListener(PRICE_UPDATE_EVENT, listener);
  });
};
